import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react"
import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

// Shared button usage guide:
// - `default`: primary CTA for a section, page, or modal
// - `outline`: the secondary button — use for the non-primary action paired with a `default` CTA
//   (e.g. a "Cancel" beside a primary action in a dialog footer, or any neutral bordered button)
// - `outline-selection`: segmented choices with a persistent neutral outlined pressed state
// - `ghost`: low-emphasis action for dense UIs such as toolbars and inline controls
// - `ghost-destructive` / `ghost-warning` / `ghost-brand`: ghost with a semantic tone —
//   toned text that persists through hover over a translucent tone wash (the
//   DropdownMenuItem destructive pattern). Use these instead of layering
//   `text-destructive` etc. onto `ghost`, whose `hover:text-content-primary`
//   strips the tone exactly when the hover wash appears.
// - `destructive`: destructive action only
// - `link`: inline textual link/action in the primary (black) content color, dotted underline
// - `link-muted`: same as `link` but in the muted (gray) content color; for secondary/inline doc links
// - `ai`: AI / Suggest CTA — white pill with brand-tinted glow shadow and brand text; reserved for AI suggestion triggers
// - `disclosure`: low-emphasis expandable rows; pair with the disclosure size and keep feature-specific layout in a wrapper
//
// Link font size is driven by the shared `size` prop: `default` = 14px (text-sm), `sm` = 12px (text-xs).
// So the four canonical inline links are: link/default (black 14px), link/sm (black 12px),
// link-muted/default (gray 14px), link-muted/sm (gray 12px).
//
// Shared button size guide:
// - `default`: standard 24px button height; preferred unless the design clearly needs otherwise
// - `toolbar`: 32px controls with a readable 14px label for prominent toolbars
// - `sm`: compact controls only
// - `lg`: intentionally larger CTAs only; do not treat as the default
// - `xl`: prominent 36px CTAs in marketing / auth / onboarding flows; use sparingly
// - `disclosure`: wrapping text disclosure with a 32px minimum target
// - `disclosure-sm`: the same target with compact text and icons
// - `icon`: standard icon-only button
//
// For reusable wrappers built on top of `Button`, prefer `@/components/ui/buttons/*`.
// Keep feature-specific buttons near their owning feature instead of moving them here.
//
// Summary of Rhythms-specific behavior:
// - gap-1 and cursor-pointer added in defaults
// - custom Rhythms size scale (xs 16px, sm 20px, default 24px, lg 28px, xl 36px, icon 24px)
// - isLoading prop added to show loading state
// - font-normal added to default size overriding font-medium
import { LoadingIcon } from "@/icons"
import { cn } from "@/lib/utils"

const disabledClasses =
  "disabled:opacity-100 disabled:bg-surface-tertiary disabled:text-content-placeholder disabled:data-[loading]:opacity-50"
const buttonVariants = cva(
  "rhythms-button focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-md border border-transparent bg-clip-padding text-sm font-normal focus-visible:ring-[2px] aria-invalid:ring-[2px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none gap-1 cursor-pointer",
  {
    variants: {
      constrainWidth: {
        true: "min-w-0 max-w-full shrink",
      },
      variant: {
        default: `bg-brand text-white hover:bg-brand/80 ${disabledClasses} disabled:data-[loading]:bg-brand disabled:data-[loading]:text-white  `,
        outline: `border-border-strong hover:bg-content-primary/5 hover:text-content-primary aria-expanded:bg-content-primary/5 aria-expanded:text-content-primary aria-pressed:bg-content-primary/5 aria-pressed:text-content-primary ${disabledClasses} disabled:data-[loading]:bg-transparent disabled:data-[loading]:text-content-primary `,
        "outline-selection": `border-border-strong text-content-primary font-normal hover:bg-content-primary/5 aria-pressed:bg-content-primary/5 aria-pressed:inset-ring-1 aria-pressed:inset-ring-content-secondary/50 ${disabledClasses}`,
        ghost:
          "hover:bg-content-primary/5 hover:text-content-primary aria-expanded:bg-content-primary/5 aria-expanded:text-content-primary aria-pressed:bg-content-primary/5 aria-pressed:text-content-primary aria-[current=page]:bg-brand/10 aria-[current=page]:hover:bg-brand/10",
        suggestion:
          "border-border bg-surface-primary text-content-primary hover:border-brand/40 hover:bg-brand/5 disabled:text-content-placeholder",
        "suggestion-follow-up":
          "bg-transparent text-content-secondary hover:bg-content-primary/5 hover:text-content-primary disabled:text-content-placeholder",
        "ghost-destructive":
          "text-destructive hover:bg-destructive/10 aria-expanded:bg-destructive/10 aria-pressed:bg-destructive/10 dark:hover:bg-destructive/20",
        "ghost-warning":
          "text-warning hover:bg-warning/10 aria-expanded:bg-warning/10 aria-pressed:bg-warning/10 dark:hover:bg-warning/20",
        "ghost-brand":
          "text-brand hover:bg-brand/10 aria-expanded:bg-brand/10 aria-pressed:bg-brand/10 dark:hover:bg-brand/20",
        disclosure:
          "text-content-secondary hover:bg-surface-secondary hover:text-content-primary disabled:bg-transparent disabled:opacity-100",
        destructive:
          "bg-destructive/90 text-white hover:bg-destructive focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-content-primary underline decoration-dotted decoration-current underline-offset-2 hover:text-brand hover:decoration-brand",
        "link-muted":
          "text-content-secondary underline decoration-dotted decoration-current underline-offset-2 hover:text-brand hover:decoration-brand",
        "link-brand": "text-brand underline-offset-2 hover:underline",
        ai: "border-white bg-surface-primary text-brand hover:bg-surface-secondary aria-expanded:bg-surface-secondary disabled:bg-surface-tertiary disabled:text-content-placeholder disabled:data-[loading]:bg-surface-primary disabled:data-[loading]:text-brand [box-shadow:0px_0px_0px_1px_color-mix(in_oklch,var(--brand)_8%,transparent),0px_1px_3px_0px_color-mix(in_oklch,var(--brand)_8%,transparent),0px_2px_5px_0px_color-mix(in_oklch,var(--brand)_6%,transparent),0px_2px_8px_0px_color-mix(in_oklch,var(--brand)_4%,transparent),0px_4px_20px_0px_color-mix(in_oklch,var(--brand)_2%,transparent),0px_10px_40px_0px_color-mix(in_oklch,var(--brand)_8%,transparent)]",
      },
      size: {
        suggestion:
          "h-auto min-h-16 w-full min-w-0 justify-start gap-3 rounded-xl px-3.5 py-4 text-left whitespace-normal",
        "suggestion-follow-up":
          "h-auto min-h-7 w-fit min-w-0 max-w-full items-start justify-start gap-2 rounded-lg px-3 py-1 text-left text-sm font-normal whitespace-normal",
        "suggestion-compact":
          "h-auto min-h-10 w-full min-w-0 justify-start gap-3 px-2.5 py-2 text-left whitespace-normal",
        default:
          "h-6 gap-1 px-2 text-sm has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-4",
        toolbar:
          "h-8 gap-1.5 px-2.5 text-[14px] [&_svg:not([class*='size-'])]:size-4",
        xs: "h-4 gap-1 rounded-sm px-2 text-[0.625rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-2.5",
        sm: "h-5 gap-1 px-2 text-xs/relaxed has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        lg: "h-7 gap-1 px-2.5 text-sm has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-4",
        xl: "h-9 gap-2 px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-5",
        disclosure:
          "min-h-8 h-auto max-w-full justify-start gap-2 px-1 py-1.5 text-sm whitespace-normal motion-reduce:transition-none",
        "disclosure-sm":
          "min-h-8 h-auto max-w-full justify-start gap-1 px-2 py-1.5 text-xs/relaxed whitespace-normal motion-reduce:transition-none [&_svg:not([class*='size-'])]:size-3",
        // Full-width choices with a preview and wrapping supporting text.
        choice:
          "h-auto w-full min-w-0 justify-start gap-3 px-2 py-2 text-left whitespace-normal",
        icon: "size-6 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-4 rounded-sm [&_svg:not([class*='size-'])]:size-2.5",
        "icon-sm": "size-5 [&_svg:not([class*='size-'])]:size-3",
        "icon-lg": "size-8 [&_svg:not([class*='size-'])]:size-4",
        "icon-xl": "size-9 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    compoundVariants: [
      {
        variant: "link-brand",
        className:
          "h-auto min-h-6 min-w-0 shrink justify-start px-0 py-0 text-left whitespace-normal",
      },
      {
        variant: ["link", "link-muted"],
        // Render inline links as real inline anchors: the base button classes set
        // `inline-flex whitespace-nowrap`, which makes multi-word link text an
        // unbreakable box that overflows narrow containers instead of wrapping.
        className:
          "inline h-auto justify-start gap-0 px-0 py-0 whitespace-normal has-data-[icon=inline-end]:pr-0 has-data-[icon=inline-start]:pl-0",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)
type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    isLoading?: boolean
  }
/**
 * A `render` target carrying an `href` is a link, not a button. Base UI's
 * `useButton` would give it button semantics it should not have: it warns that
 * the element is not a native `<button>`, and silencing that with
 * `nativeButton={false}` adds `role="button"`, so screen readers announce a
 * link as a button and drop it from the links list. The `type="button"` below
 * is not a valid attribute on `<a>` either. Style the anchor directly instead —
 * the same `buttonVariants`-on-a-`<Link>` pattern used in `marketplace/page.tsx`.
 */
function isLinkRender(render: unknown): render is ReactElement<{
  className?: string
  href?: string
  children?: ReactNode
}> {
  return (
    isValidElement(render) &&
    typeof (render.props as { href?: unknown }).href === "string"
  )
}

/** Base UI `Button` props that describe button semantics and mean nothing on an `<a>`. */
const BUTTON_ONLY_PROPS = new Set([
  "render",
  "nativeButton",
  "focusableWhenDisabled",
])

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      isLoading,
      constrainWidth,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const variantClassName = cn(
      buttonVariants({ variant, size, constrainWidth }),
      className
    )

    if (isLinkRender(props.render)) {
      const link = props.render
      const forwarded = Object.fromEntries(
        Object.entries(props).filter(([key]) => !BUTTON_ONLY_PROPS.has(key))
      )
      const linkChildren = link.props.children ?? children

      return cloneElement(link, {
        ...forwarded,
        // The render target's own props win, so an explicit href/target/onClick
        // on the link is never clobbered by one passed to `Button`.
        ...link.props,
        ref,
        "data-slot": "button",
        "data-loading": isLoading || undefined,
        // `disabled` is not a valid attribute on <a>, so convey the state to
        // assistive tech rather than emitting invalid markup.
        "aria-disabled": disabled || isLoading || undefined,
        className: cn(variantClassName, link.props.className),
        children: isLoading ? (
          <>
            <LoadingIcon className="size-4 animate-spin" />
            {linkChildren}
          </>
        ) : (
          linkChildren
        ),
      } as Partial<typeof link.props> & { ref?: Ref<HTMLElement> })
    }

    return (
      <ButtonPrimitive
        ref={ref}
        data-slot="button"
        data-loading={isLoading || undefined}
        className={variantClassName}
        disabled={disabled || isLoading}
        type="button"
        {...props}
      >
        {isLoading ? <LoadingIcon className="size-4 animate-spin" /> : null}
        {children}
      </ButtonPrimitive>
    )
  }
)
Button.displayName = "Button"
export { Button, buttonVariants }
